import { Button, Drawer, Form, Input, Tooltip, InputNumber, Switch, Spin } from "antd";
import React, { forwardRef, ReactNode, useImperativeHandle, useState } from "react";
import styled from "styled-components";
import { ButtonShape } from "antd/lib/button/button";
import { useTheme } from "../contexts/ThemeProvider";

const Container = styled.div`
  .ant-form-item-label > label {
    text-transform: capitalize;
  }

  .ant-spin-nested-loading {
    position: relative;
  }

  .ant-spin-spinning {
    position: fixed !important;
    transform: scale(1.25);
  }
`;

interface FormInput {
  name: string;
  type?: 'button' | 'checkbox' | 'color' | 'date' | 'datetime-local' | 'email' | 'file' | 'hidden' | 'image' | 'month' | 'number' | 'password' | 'radio' | 'range' | 'reset' | 'search' | 'submit' | 'tel' | 'text' | 'textarea' | 'time' | 'url' | 'week';
  required?: boolean;
  props?: object;
}

interface RecordDrawerProps {
  recordName: string;
  actionName?: string;
  requieredTextFields?: string[];
  inputs?: FormInput[];
  handleRecordAction: any;
  afterSave?: (res: any) => void;
  recordId?: number | string;
  afterFormReset?: () => void;
  children?: ReactNode;
  tooltip?: string;
  disabled?: boolean;
  refFocus?: any;
  refDrawer?: any;
  handleValuesChange?: any;
  btnSize?: 'small' | 'middle' | 'large';
  btnType?: 'primary' | 'link' | 'default' | 'text' | 'ghost' | 'dashed';
  btnGhost?: boolean;
  btnText?: string;
  btnShape?: ButtonShape;
  btnIcon?: ReactNode;
  customButton?: ReactNode;
  onClose?: any;
  customFooter?: ReactNode;
  saveButtonText?: string;
  drawerWidth?: number | string;
  useMultipartFormData?: boolean;
  primaryKeyName?: string;
  btnStyle?: any;
  extraActions?: any;
  drawerExtra?: ReactNode;
}

const InputComponents: {[inputName: string]: any} = {
  // date: (props: any) => <DatePicker {...props} format="LL" />,
  number: InputNumber,
  password: Input.Password,
  textarea: Input.TextArea,
  checkbox: (props: any) => <Switch {...props} style={{}} />,
  text: Input,
}

function RDrawer(props: RecordDrawerProps, ref: any) {
  const {
    recordName,
    handleRecordAction,
    requieredTextFields = [],
    inputs = [],
    actionName = 'Create',
    btnType = 'primary',
    btnSize = 'middle',
    useMultipartFormData = false,
    primaryKeyName,
  } = props;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form] = Form.useForm();
  const { currentTheme } = useTheme();

  const handleBtnClick = (e: any) => {
    e.stopPropagation();
    setDrawerOpen(true);
    setTimeout(() => {
      if (props.refFocus) props.refFocus.current!.focus({ cursor: 'all' });
    }, 250);
  }

  useImperativeHandle(ref, () => {
    return {
      open: () => {
        setDrawerOpen(true);
        setTimeout(() => {
          if (props.refFocus) props.refFocus.current!.focus({ cursor: 'all' });
        }, 250);
      },
      close: () => {
        setDrawerOpen(false);
        props.onClose();
      },
      setFieldsValue: (data: any) => {
        form.setFieldsValue(data);
      },
      getFieldsValue: form.getFieldsValue,
      resetFields: form.resetFields
    }
  }, []);

  return (
    <>
      <Tooltip title={props.tooltip}>
        {!!props.customButton ? (
          <div onClick={handleBtnClick}>
            {props.customButton}
          </div>
        ) : (
          <Button
            type={btnType}
            size={btnSize}
            shape={props.btnShape}
            icon={props.btnIcon}
            ghost={props.btnGhost || false}
            onClick={handleBtnClick}
            disabled={props.disabled}
            style={props.btnStyle}
          >
            {props.btnText ? props.btnText : `${actionName} ${recordName}`}
          </Button>
        )}
      </Tooltip>

      <Drawer
        title={`${actionName} ${recordName}`}
        placement="right"
        width={props.drawerWidth || 450}
        onClose={(e) => {
          e.stopPropagation();
          setDrawerOpen(false);
          props.onClose();
        }}
        visible={drawerOpen}
        extra={props.drawerExtra}
      >
        <Container>
        <Form
          form={form}
          style={{ width: '100%', flex: 1, height: '100%' }}
          name="basic"
          onFinish={async (rawData) => {
            // console.log('>>>>> rawData', rawData);
            let data: any = {};
            let config;
            if (useMultipartFormData) {
              data = new FormData();
              Object.entries(rawData).forEach(([k,v]: any) => {
                const isFileUrl = !!v && typeof v === 'string' && v.includes('https://');
                if (v !== undefined && v !== null && !isFileUrl) {
                  if (Array.isArray(v)) {
                    // For arrays, append each element with the same key name (Django REST framework style)
                    // Skip empty arrays entirely (works with PUT requests)
                    if (v.length > 0) {
                      v.forEach((item) => {
                        data.append(k, item);
                      });
                    }
                  } else {
                    data.append(k, v);
                  }
                }
              });
              config= {
                "headers": {
                  "content-type": 'multipart/form-data; boundary=----WebKitFormBoundaryqTqJIxvkWFYqvP5s'
                }
              }
            } else {
              for (const key in rawData) {
                if (Object.prototype.hasOwnProperty.call(rawData, key) && rawData[key] !== undefined) {
                  data[key] = rawData[key];
                }
              }
            }
            setIsSaving(true);
            try {
              if (!!props.recordId) {
                await handleRecordAction(props.recordId, data, primaryKeyName, config, props.afterSave);
              } else {
                await handleRecordAction(data, props.afterSave, config);
              }
              form.resetFields();
              setDrawerOpen(false);
              if (props.afterFormReset) props.afterFormReset();
            } finally {
              setIsSaving(false);
            }
          }}
          autoComplete="off"
          layout="vertical"
          onValuesChange={props.handleValuesChange}
        >
          <Spin spinning={isSaving} tip="Saving...">
            <div style={{ pointerEvents: isSaving ? 'none' : undefined }}>
              {requieredTextFields.map(fieldName => (
                <Form.Item
                  label={fieldName}
                  name={fieldName}
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
              ))}

              {props.children}

              {inputs.map(input => {
                const InputComponent = InputComponents[input.type || 'text'] || Input;
                return (
                  <Form.Item
                    label={input.name.replaceAll("_", " ")}
                    name={input.name}
                    rules={[{ required: input.required !== undefined ? input.required : true }]}
                  >
                    <InputComponent
                      type={input.type || 'text'}
                      style={{ width: '100%' }}
                      {...input.props}
                    />
                  </Form.Item>
                );
              })}

              <br/><br/><br/><br/><br/>
            </div>
          </Spin>

          {props.customFooter ? props.customFooter : (
          <div
            style={{
              padding: '12px 24px 16px 24px',
              backgroundColor: currentTheme === 'dark' ? 'rgb(42,42,42)' :'#f3f3f3',
              margin: '0 -24px', position: 'fixed', bottom: 0, width: props.drawerWidth || 450,
              display: 'flex',
            }}
          >
            <div className="flx1 gap-xs disp-flex h-stack-strict">
              <Form.Item style={{ display: 'inline-block', margin: 0 }}>
                <Button type="primary" htmlType="submit" loading={isSaving} disabled={isSaving}>
                  {props.saveButtonText ? props.saveButtonText : actionName}
                </Button>
              </Form.Item>
              <Button onClick={() => setDrawerOpen(false)} disabled={isSaving}>
                Cancel
              </Button>
            </div>

            <div>
              {props.extraActions}
            </div>
          </div>
          )}
        </Form>
        </Container>
      </Drawer>
    </>
  )
}

const FormDrawer = forwardRef(RDrawer);
export default FormDrawer;

